import { ScrollReveal, StaggerContainer, StaggerItem, TextReveal } from '@/components/motion'
import { HALFTONE_STYLE, Highlight } from '@/components/landing/about/Highlight'
import type { IndustryData } from '@/data/industries'

const BACKDROPS = ['var(--color-landing-accent)', '#ff00f2', 'var(--color-landing-secondary)', '#0a0a0a'] as const

/** "Chief Medical Officer" → "CMO" (role initials, at most three). */
export function roleInitials(role: string): string {
  return role
    .replace(/[^A-Za-z ]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((w) => w[0]!.toUpperCase())
    .join('')
}

/** "Clinical Documentation (45)" → ["Clinical Documentation", "45"]. */
function splitCount(entry: string): [string, string | null] {
  const match = /^(.*?)\s*\(([^)]+)\)\s*$/.exec(entry)
  return match ? [match[1]!, match[2]!] : [entry, null]
}

/**
 * The about page's team pattern for an industry's roles: sticky heading
 * column (quote naming the model organisation, its team sizes) beside a
 * grid of role tiles on solid brand backdrops. Tiles carry the role's
 * initials, not invented people.
 */
export function RoleGrid({ industry }: { industry: IndustryData }) {
  const company = industry.sampleCompany
  const facts = company.structure.map(splitCount).filter(([, n]) => n !== null).slice(0, 4)

  return (
    <section aria-labelledby="roles-title" className="landing-section">
      <div className="landing-container grid items-start gap-14 lg:grid-cols-[minmax(0,0.4fr)_minmax(0,1fr)] lg:gap-20">
        <div className="lg:sticky lg:top-28">
          <TextReveal as="h2" className="landing-text-display-xl text-[var(--color-landing-gray-900)] dark:text-white">
            <span id="roles-title">
              Built for <Highlight color="purple">every role.</Highlight>
            </span>
          </TextReveal>
          <ScrollReveal direction="none" delay={0.15}>
            <p className="mt-8 max-w-[36ch] border-l border-[var(--color-landing-gray-300)] pl-5 landing-text-body-lg text-[color-mix(in_srgb,var(--color-landing-gray-900)_66%,transparent)] dark:border-[var(--color-landing-gray-700)] dark:text-[color-mix(in_srgb,white_62%,transparent)]">
              Modelled on {company.name}: {company.type.toLowerCase()}, {company.size}.
            </p>
            {facts.length > 0 && (
              <dl className="mt-10 grid max-w-sm grid-cols-2 gap-6">
                {facts.map(([label, count]) => (
                  <div key={label}>
                    <dt className="text-sm text-[color-mix(in_srgb,var(--color-landing-gray-900)_66%,transparent)] dark:text-[color-mix(in_srgb,white_62%,transparent)]">
                      {label}
                    </dt>
                    <dd className="mt-1 font-[family-name:var(--font-landing-subdisplay)] text-xl font-bold uppercase text-[var(--color-landing-gray-900)] dark:text-white">
                      {count}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </ScrollReveal>
        </div>

        <StaggerContainer className="grid grid-cols-2 gap-x-4 gap-y-12 md:gap-x-6 md:gap-y-16" amount={0.05}>
          {industry.personas.map((persona, index) => (
            <StaggerItem key={persona.role}>
              <figure className="group">
                <div
                  aria-hidden
                  className="relative aspect-[4/3] overflow-hidden [container-type:inline-size] dark:ring-1 dark:ring-inset dark:ring-white/10"
                  style={{ background: BACKDROPS[index % BACKDROPS.length] }}
                >
                  <div
                    className="absolute inset-0 opacity-60 transition-opacity duration-300 ease-out [@media(hover:hover)_and_(pointer:fine)]:group-hover:opacity-100"
                    style={HALFTONE_STYLE}
                  />
                  <span className="absolute bottom-[6%] left-[6%] font-[family-name:var(--font-landing-display)] text-[24cqw] uppercase leading-none tracking-[-0.04em] text-white">
                    {roleInitials(persona.role)}
                  </span>
                  <span className="absolute right-[6%] top-[7%] bg-white px-[0.6em] py-[0.35em] text-[max(10px,3.4cqw)] font-bold uppercase tracking-[0.04em] text-[#0a0a0a]">
                    {persona.timeSaved}
                  </span>
                </div>
                <figcaption className="mt-4">
                  <h3 className="font-[family-name:var(--font-landing-subdisplay)] text-lg font-bold uppercase leading-tight tracking-[-0.01em] text-[var(--color-landing-gray-900)] dark:text-white md:text-xl">
                    {persona.role}
                  </h3>
                  <p className="mt-1 text-sm text-[color-mix(in_srgb,var(--color-landing-gray-900)_66%,transparent)] dark:text-[color-mix(in_srgb,white_62%,transparent)] md:text-base">
                    {persona.description}. Saves {persona.timeSaved}.
                  </p>
                </figcaption>
              </figure>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </section>
  )
}
